/**
 * In-memory POS for screens and tests (VITE_USE_MOCK). Prices follow the POS sale-page rules closely
 * enough to look right: pack price + plus × qty + boxes over the quota at menu price; Lalamove 65 with a pin,
 * DHL 100; coupons frozen at entry via a token tied to the pack; COD +30.
 * Catalog rows are a subset of the POS beta catalog (26 ก.ย.).
 */
import { bangkokTime, cardExpiry, defaultDate, isLocked } from '../../lib/order'
import { dayKey } from '../../lib/inbox'
import { PosApiError } from './http'
import type {
  ChatOrderRef,
  PosApi,
  PosCartBody,
  PosCatalog,
  PosChatOrderState,
  PosContext,
  PosCouponCheck,
  PosCouponCheckBody,
  PosCustomer,
  PosCustomerCreate,
  PosCustomerCreated,
  PosDelivery,
  PosHistory,
  PosHistoryOrder,
  PosCard,
  PosHoldCart,
  PosHoldEditBody,
  PosOpenOrder,
  PosOpenPack,
  PosOrderBody,
  PosOrderResult,
  PosOrderStatus,
  PosPayBody,
  PosPinResult,
  PosPinSet,
  PosQr,
  PosQuote,
  PosSharedPlace,
  PosRenewBody,
} from './types'

const clone = <T>(v: T): T => (v === null || v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T))

export const MOCK_CATALOG: PosCatalog = {
  env: 'mock',
  set_menus: [
    { id: 126, name: 'ทดลองทาน 9 แพ็ค', price: 540, meals: 9, rounds: 1, days: 3 },
    { id: 104, name: 'Pro 7 day 21 มื้อ ลด 5%', price: 997, meals: 21, rounds: 1, days: 7 },
    { id: 105, name: 'Pro 15 day 45 มื้อ ลด 10%', price: 2025, meals: 45, rounds: 1, days: 15 },
    { id: 106, name: 'Pro 30 day 90 มื้อ ลด 15%', price: 3825, meals: 90, rounds: 2, days: 30 },
    { id: 110, name: 'Pack 7day กับข้าว 21แพ็ค', price: 1050, meals: 21, rounds: 1, days: 7 },
    { id: 122, name: 'Keto 15 Meal 900 บาท 60.-/Pack', price: 900, meals: 15, rounds: 1, days: 5 },
  ],
  menus: [
    { id: 101, code: 'เพรา', name: 'P1.กะเพราไก่สับ', price: 50, plus: 0, type: 'sideDish', meat_type: 'chicken', order: 1, name_en: 'Basil Minced Chicken' },
    { id: 105, code: 'จัง', name: 'P2.ไก่ย่างโคชูจัง', price: 50, plus: 0, type: 'sideDish', meat_type: 'chicken', order: 2, name_en: 'Gochujang Grilled Chicken' },
    { id: 106, code: 'เทียม', name: 'P3.ไก่สับกระเทียมพริกไทย', price: 50, plus: 0, type: 'sideDish', meat_type: 'chicken', order: 3 },
    { id: 181, code: 'เขียว', name: 'P4.แกงเขียวหวานไก่', price: 50, plus: 0, type: 'sideDish', meat_type: 'chicken', order: 4 },
    { id: 237, code: 'แซล', name: 'P5.แซลมอนย่างซีอิ๊วญี่ปุ่น', price: 95, plus: 25, type: 'sideDish', meat_type: 'fish', order: 5, name_en: 'Grilled Salmon Japanese Soy Sauce' },
    { id: 240, code: 'ดอลลี่', name: 'P6.ปลาดอลลี่นึ่งมะนาว', price: 60, plus: 10, type: 'sideDish', meat_type: 'fish', order: 6 },
    { id: 250, code: 'หมูดำ', name: 'P7.สเต๊กหมูพริกไทยดำ', price: 55, plus: 5, type: 'sideDish', meat_type: 'pork', order: 7, name_en: 'Black Pepper Pork Steak' },
    { id: 260, code: 'ผัก', name: 'P8.ผัดผักรวมเต้าหู้', price: 50, plus: 0, type: 'sideDish', meat_type: 'veg', order: 8 },
    { id: 300, code: 'บราวนี่', name: 'บราวนี่คีโต', price: 45, plus: 0, type: 'snacks', meat_type: null, order: 20 },
  ],
  channels: [
    { id: 1, type: 'facebook', name: 'Cleanfit Delivery อาหารลดน้ำหนัก' },
    { id: 5, type: 'line', name: 'Cleanfit' },
    { id: 7, type: 'facebook', name: 'Cleanfit Healthy อาหารสุขภาพ' },
    { id: 10, type: 'instagram', name: 'Instagram' },
  ],
  payment_methods: ['BankTransfer', 'CashOnDelivery', 'PendingTransfer', 'CardLink'],
  banks: ['01'],
  cod_fee: 30,
  delivery_slots: [
    { time: '11:00', label: '10:00 - 12:00' },
    { time: '14:00', label: '13:00 - 15:00' },
    { time: '16:00', label: '15:00 - 17:00' },
    { time: '18:00', label: '17:00 - 19:00' },
  ],
  stale: false,
}

/** Drawn dish photos for mock mode (the expanded menu picker); the real ones are the POS filestore `img` URLs. */
function mockDishImg(tint: string, seed: number): string {
  const r = 58 + (seed % 5) * 6
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360" viewBox="0 0 480 360">' +
    '<rect width="480" height="360" fill="#eef1ec"/>' +
    '<rect x="44" y="34" width="392" height="292" rx="26" fill="#2f3b35"/>' +
    '<rect x="58" y="48" width="364" height="264" rx="18" fill="#3c4a43"/>' +
    '<rect x="72" y="62" width="190" height="236" rx="14" fill="#f7f4ec"/>' +
    `<circle cx="${150 + (seed % 3) * 14}" cy="178" r="${r}" fill="${tint}"/>` +
    `<circle cx="${128 + (seed % 4) * 10}" cy="150" r="${Math.round(r / 3)}" fill="#fff" opacity=".35"/>` +
    '<rect x="276" y="62" width="132" height="112" rx="14" fill="#8cc084"/>' +
    '<rect x="276" y="186" width="132" height="112" rx="14" fill="#f1eee4"/>' +
    '<circle cx="342" cy="118" r="28" fill="#6fae6a"/><circle cx="342" cy="242" r="30" fill="#e9dcc2"/>' +
    '</svg>'
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}
const DISH_TINT: Record<string, string> = { chicken: '#e9b872', pork: '#c98b6b', fish: '#ef9a74', veg: '#b5d49a' }
for (const m of MOCK_CATALOG.menus) m.img = mockDishImg(DISH_TINT[m.meat_type ?? ''] ?? '#b88a5a', m.id)

interface MockCustomer extends PosCustomer {
  mobile: string
  /** A pin outside the Lalamove zone: the system cannot price it (the manual fee with a reason stays for this). */
  outOfZone?: boolean
}

const CUSTOMERS: MockCustomer[] = [
  {
    id: '240518', name: 'มายด์ ศรีสุข', mobile: '0812344521', mobile_masked: '•••-•••-4521',
    address: '99/12 ซ.บางนา-ตราด 25 แขวงบางนา เขตบางนา กทม. 10260', has_pin: true, channel: 'line', contact: 'คุณมายด์',
    last_order_at: '2026-08-13 10:12:00', created_at: '2026-05-02 09:00:00', deleted: false,
  },
  {
    id: '240777', name: 'พิม ชนก', mobile: '0891112233', mobile_masked: '•••-•••-2233',
    address: '12 ถ.นิมมานเหมินท์ ต.สุเทพ อ.เมืองเชียงใหม่ จ.เชียงใหม่ 50200', has_pin: false, channel: 'facebook',
    contact: 'Pim Chanok', last_order_at: null, created_at: '2026-09-01 12:00:00', deleted: false,
  },
  // phone button under customer messages (mock chats c152–c155, ../mockData.ts)
  {
    id: '240950', name: 'ฝน ใจดี', mobile: '0858836069', mobile_masked: '•••-•••-6069',
    address: '55 ซ.สุขุมวิท 101 แขวงบางจาก เขตพระโขนง กทม. 10260', has_pin: true, channel: 'line', contact: 'ฝน',
    last_order_at: null, created_at: '2026-09-20 15:00:00', deleted: false,
  },
  {
    id: '240960', name: 'ต้น ทดสอบ', mobile: '0812223344', mobile_masked: '•••-•••-3344',
    address: '8 ถ.พระราม 9 แขวงห้วยขวาง เขตห้วยขวาง กทม. 10310', has_pin: true, channel: 'line', contact: 'Ton',
    last_order_at: '2026-09-20 18:30:00', created_at: '2026-07-01 10:00:00', deleted: false,
  },
  {
    id: '240970', name: 'บริษัท สุขภาพดี', mobile: '021234567', mobile_masked: '•••-•••-4567',
    address: 'อาคารสาทรซิตี้ ชั้น 12 เขตสาทร กทม. 10120', has_pin: true, channel: 'facebook', contact: 'สุขภาพดี',
    last_order_at: '2026-09-10 12:00:00', created_at: '2026-03-01 09:00:00', deleted: false,
  },
  {
    id: '240971', name: 'สุขภาพดี สาขา 2', mobile: '021234567', mobile_masked: '•••-•••-4567',
    address: 'อาคารสาทรซิตี้ ชั้น 14 เขตสาทร กทม. 10120', has_pin: true, channel: 'facebook', contact: null,
    last_order_at: null, created_at: '2026-06-01 09:00:00', deleted: false,
  },
  {
    id: '240901', name: 'บอส ทดสอบ', mobile: '0865554321', mobile_masked: '•••-•••-4321',
    address: 'คอนโดริทึ่ม สาทร ห้อง 1203 เขตสาทร กทม. 10120', has_pin: true, channel: 'line', contact: 'คุณบอส',
    last_order_at: '2026-09-06 18:00:00', created_at: '2026-06-10 11:00:00', deleted: false,
  },
  // a pin outside the Lalamove zone (the manual fee path)
  {
    id: '240990', name: 'ปราง อยุธยา', mobile: '0867778899', mobile_masked: '•••-•••-8899',
    address: '9 ถ.โรจนะ ต.ประตูชัย อ.พระนครศรีอยุธยา จ.พระนครศรีอยุธยา 13000', has_pin: true, outOfZone: true,
    channel: 'line', contact: 'ปราง', last_order_at: '2026-08-01 10:00:00', created_at: '2026-05-01 10:00:00', deleted: false,
  },
]

/** The place the customer gave in the chat (GET …/shared-pin): c103 shared "คอนโดบางนา" (../mockData.ts). */
const SHARED_PLACES: Record<string, PosSharedPlace> = {
  c103: {
    lat: 13.668431, lng: 100.634617, source: 'location', address: 'ถนนบางนา-ตราด แขวงบางนาใต้ เขตบางนา กรุงเทพมหานคร 10260',
    message_id: 'c103-loc', at: '2026-09-26T06:20:00Z',
  },
}

/** conversation id → POS channel + linked customer (mock conversations in ../mockData.ts). */
const CONVS: Record<string, { type: 'facebook' | 'line' | 'instagram' | null; channelId: number | null; customer: string | null }> = {
  c101: { type: 'facebook', channelId: 7, customer: null },
  c102: { type: 'line', channelId: 5, customer: '240518' },
  c103: { type: 'line', channelId: 5, customer: null },
  c104: { type: 'line', channelId: 5, customer: '240901' },
  c105: { type: 'facebook', channelId: 7, customer: null },
  c106: { type: 'instagram', channelId: 10, customer: null },
  c107: { type: null, channelId: null, customer: null },
  c152: { type: 'line', channelId: 5, customer: null },
  c153: { type: 'facebook', channelId: 7, customer: null },
  c154: { type: 'line', channelId: 5, customer: '240960' },
  c155: { type: 'facebook', channelId: 7, customer: '240777' },
}

const HISTORY: Record<string, { orders: PosHistoryOrder[]; open_packs: PosOpenPack[] }> = {
  '240518': {
    orders: [
      { order_delivery_id: 279801, order_code: '20260813-07', created_at: '2026-08-12 19:40:00', delivery_date: '2026-08-13', delivery_time: '11:00', carrier: 'lalamove', status: '4', status_text: 'จัดส่งเสร็จสิ้น', total: 2105, meals: 45, set_menu_name: 'Pro 15 day 45 มื้อ ลด 10%', channel: 'line', source: 'pos', order_header_id: 271900 },
      { order_delivery_id: 278312, order_code: '20260728-12', created_at: '2026-07-27 20:05:00', delivery_date: '2026-07-28', delivery_time: '14:00', carrier: 'lalamove', status: '4', status_text: 'จัดส่งเสร็จสิ้น', total: 1077, meals: 21, set_menu_name: 'Pro 7 day 21 มื้อ ลด 5%', channel: 'line', source: 'pos', order_header_id: 270611 },
      { order_delivery_id: 276020, order_code: '20260702-03', created_at: '2026-07-01 18:30:00', delivery_date: '2026-07-02', delivery_time: '11:00', carrier: 'lalamove', status: '4', status_text: 'จัดส่งเสร็จสิ้น', total: 605, meals: 9, set_menu_name: 'ทดลองทาน 9 แพ็ค', channel: 'facebook', source: 'pos', order_header_id: 268540 },
    ],
    open_packs: [
      { order_header_id: 271900, set_menu_name: 'Pro 15 day 45 มื้อ ลด 10%', meals_quota: 45, meals_remaining: 24, rounds: 2, rounds_used: 1, created_at: '2026-08-12 19:40:00' },
    ],
  },
  '240901': {
    orders: [
      { order_delivery_id: 280010, order_code: '20260906-21', created_at: '2026-09-05 21:10:00', delivery_date: '2026-09-06', delivery_time: '18:00', carrier: 'lalamove', status: '-1', status_text: 'ยกเลิก', total: 1077, meals: 21, set_menu_name: 'Pro 7 day 21 มื้อ ลด 5%', channel: 'line', source: 'pos', order_header_id: 271990 },
    ],
    open_packs: [],
  },
}

const COUPONS: Record<string, { unit: 'percen' | 'bath'; benefits: number; name: string }> = {
  UPNC: { unit: 'percen', benefits: 5, name: 'ลด 5%' },
  WELCOME50: { unit: 'bath', benefits: 50, name: 'ลด 50 บาท' },
}

export interface MockPosOptions {
  latencyMs?: number
  /** "Now" for dates (tests pass their fixed clock). */
  now?: number
}

const DAY = 86400000

/** catalog.delivery like POS chat_v1 round 5, relative to `now`; the day after tomorrow is locked (Lalamove). */
export function mockDelivery(now: number): PosDelivery {
  const today = dayKey(now)
  return {
    today,
    server_time: `${today} ${bangkokTime(now)}:00`,
    default_date: defaultDate(now),
    default_time: '11:00',
    date_min: today,
    date_max: dayKey(now + 90 * DAY),
    slots: MOCK_CATALOG.delivery_slots,
    locked_dates: [{ date: dayKey(now + 2 * DAY), carrier: 'lalamove', locked_at: `${today} 09:00:00` }],
    advisory: {
      enforced: false,
      web_close_time: '21:30',
      weekoff: { dhl: ['sat', 'sun'], lalamove: ['sat'] },
      holidays: [],
      closed_days: [],
    },
    source: 'pos',
  }
}

export class MockPosApi implements PosApi {
  private readonly latency: number
  private readonly now: number
  private readonly cat: PosCatalog
  private readonly customers = new Map<string, MockCustomer>(CUSTOMERS.map((c) => [c.id, clone(c)]))
  private readonly convs = clone(CONVS)
  private readonly history = clone(HISTORY)
  private readonly orders = new Map<string, PosOrderResult>()
  private readonly chatOrders = new Map<string, ChatOrderRef[]>()
  /** Test hooks: the waiting save per conversation (context.open_order) and chat order states by id. */
  readonly openOrders = new Map<string, PosOpenOrder>()
  readonly chatStates = new Map<string, PosChatOrderState>()
  private seq = 0
  private qrSeq = 0
  /** Pins set from the chat (setCustomerPin). */
  private readonly pins = new Map<string, { lat: number; lng: number }>()
  /** "แก้ไขออเดอร์" answers by order + click (the same click again replays). */
  private readonly edits = new Map<string, PosChatOrderState>()
  /** Test hook: a QR made counts as sent to the customer (the edit's "send the new QR" note). */
  qrSent = true
  /** Test hook: a card link counts as sent to the customer (an edit's "send the new link" note). */
  linkSent = true
  /** Test hook: POS beta without Beam's test mode (the card's staff-only warning). */
  cardPaySafe = false
  private cardSeq = 0
  /** Test hooks: fail the next calls of a method with these errors (then behave normally). */
  readonly failNext: Partial<Record<keyof PosApi, PosApiError[]>> = {}
  /** Every call, for tests. */
  readonly calls: { method: keyof PosApi; body: unknown }[] = []

  constructor(opts: MockPosOptions = {}) {
    this.latency = opts.latencyMs ?? 150
    this.now = opts.now ?? Date.now()
    this.cat = { ...MOCK_CATALOG, delivery: mockDelivery(this.now) }
  }

  private async tick<T>(method: keyof PosApi, body: unknown, fn: () => T): Promise<T> {
    this.calls.push({ method, body: clone(body) })
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
    const f = this.failNext[method]?.shift()
    if (f) throw f
    return clone(fn())
  }

  private conv(id: string) {
    const c = this.convs[id] ?? { type: 'line' as const, channelId: 5, customer: null }
    this.convs[id] = c
    return c
  }

  private pub(c: MockCustomer): PosCustomer {
    const { mobile: _m, outOfZone: _z, ...rest } = c
    return rest
  }

  catalog(): Promise<PosCatalog> {
    return this.tick('catalog', null, () => this.cat)
  }

  context(conversationId: string): Promise<PosContext> {
    return this.tick('context', conversationId, () => {
      const c = this.conv(conversationId)
      const channel = MOCK_CATALOG.channels.find((x) => x.id === c.channelId) ?? null
      return {
        conversation_id: conversationId,
        env: 'mock',
        contact_name: null,
        can_link: true,
        customer_id: c.customer,
        linked_at: null,
        channel,
        channel_error: channel ? null : { reason: 'channelUnsupported', message: 'แชทช่องทางนี้ยังลงออเดอร์ใน POS ไม่ได้' },
        staff: { pos_staff_id: 49, source: 'fallback' },
        staff_error: null,
        chat_orders: this.chatOrders.get(conversationId) ?? [],
        open_order: this.openOrders.get(conversationId) ?? null,
        held_orders: (this.chatOrders.get(conversationId) ?? []).filter((o) => o.status === 'held'),
      }
    })
  }

  searchCustomers(q: string, opts: { exactMobile?: boolean } = {}): Promise<PosCustomer[]> {
    return this.tick('searchCustomers', opts.exactMobile ? { q, exact: true } : q, () => {
      const t = q.trim().replace(/[\s-]/g, '')
      if (t.length < 2) return []
      return [...this.customers.values()]
        .filter((c) =>
          opts.exactMobile
            ? c.mobile === t
            : /^\d+$/.test(t)
              ? c.mobile.includes(t)
              : `${c.name} ${c.contact ?? ''}`.includes(q.trim()),
        )
        .map((c) => this.pub(c))
    })
  }

  createCustomer(body: PosCustomerCreate): Promise<PosCustomerCreated> {
    return this.tick('createCustomer', body, () => {
      const mobile = body.mobile.replace(/\D/g, '')
      const dup = [...this.customers.values()].find((c) => c.mobile === mobile)
      if (dup) {
        throw new PosApiError(409, 'เบอร์นี้มีลูกค้าใน POS อยู่แล้ว เลือกใช้ลูกค้าคนเดิมได้', 'mobileExists', null, {
          existing: this.pub(dup),
        })
      }
      const id = String(250000 + this.customers.size)
      const address =
        body.shipping === 'dhl' && body.dhl
          ? `${body.dhl.address} ตำบล${body.dhl.tambon} อำเภอ${body.dhl.amphoe} จ.${body.dhl.province} ${body.dhl.postcode}`
          : (body.address ?? '')
      const c: MockCustomer = {
        id, name: `${body.fname} ${body.lname}`.trim(), mobile, mobile_masked: `•••-•••-${mobile.slice(-4)}`, address,
        has_pin: body.lat != null && body.lng != null, channel: this.conv(body.conversation_id).type, contact: null,
        last_order_at: null, created_at: '2026-09-26 10:00:00', deleted: false,
      }
      this.customers.set(id, c)
      if (body.link !== false) this.conv(body.conversation_id).customer = id
      const warnings = body.shipping === 'lalamove' && !c.has_pin ? ['ไม่มีหมุด — ส่ง Lalamove ต้องใส่หมุดก่อนลงออเดอร์'] : []
      return { customer: this.pub(c), location_id: 900, warnings, linked: body.link !== false }
    })
  }

  /** POS chat_v1 customers/<id>/pin: a customer with no pin gets one · the same pin again = changed false · another
   *  pin already there = 409 pinExists. */
  setCustomerPin(customerId: string, body: PosPinSet): Promise<PosPinResult> {
    return this.tick('setCustomerPin', { customerId, ...body }, () => {
      if (!(body.lat >= 5 && body.lat <= 21 && body.lng >= 97 && body.lng <= 106)) {
        throw new PosApiError(422, 'หมุดต้องเป็นพิกัดในประเทศไทย (เช่น 13.7563, 100.5018) หรือวางลิงก์ Google Maps', 'pinInvalid')
      }
      const c = this.customers.get(customerId)
      if (!c) throw new PosApiError(404, 'ไม่พบลูกค้าใน POS', 'customerNotFound')
      const was = this.pins.get(customerId)
      if (c.has_pin) {
        if (was && Math.abs(was.lat - body.lat) < 1e-6 && Math.abs(was.lng - body.lng) < 1e-6) {
          return { customer: this.pub(c), changed: false, km: null, warnings: [] }
        }
        throw new PosApiError(409, 'ลูกค้ามีหมุดใน POS อยู่แล้ว (มีคนใส่ไว้ก่อน) ถ้าต้องการย้ายหมุด แก้ที่หน้าลูกค้าใน POS', 'pinExists')
      }
      c.has_pin = true
      this.pins.set(customerId, { lat: body.lat, lng: body.lng })
      return { customer: this.pub(c), changed: true, km: 8.4, warnings: [] }
    })
  }

  sharedPin(conversationId: string): Promise<PosSharedPlace | null> {
    return this.tick('sharedPin', conversationId, () => SHARED_PLACES[conversationId] ?? null)
  }

  linkCustomer(conversationId: string, customerId: string): Promise<PosCustomer> {
    return this.tick('linkCustomer', { conversationId, customerId }, () => {
      const c = this.customers.get(customerId)
      if (!c) throw new PosApiError(404, 'ไม่พบลูกค้าใน POS', 'customerNotFound')
      this.conv(conversationId).customer = customerId
      return this.pub(c)
    })
  }

  unlinkCustomer(conversationId: string): Promise<void> {
    return this.tick('unlinkCustomer', conversationId, () => {
      this.conv(conversationId).customer = null
    })
  }

  customerOrders(customerId: string, limit = 20): Promise<PosHistory> {
    return this.tick('customerOrders', customerId, () => {
      const c = this.customers.get(customerId)
      if (!c) throw new PosApiError(404, 'ไม่พบลูกค้าใน POS', 'customerNotFound')
      const h = this.history[customerId] ?? { orders: [], open_packs: [] }
      return { customer: this.pub(c), orders: h.orders.slice(0, limit), open_packs: h.open_packs }
    })
  }

  orderStatus(key: string): Promise<PosOrderStatus> {
    return this.tick('orderStatus', key, () => {
      for (const h of Object.values(this.history)) {
        const o = h.orders.find((x) => x.order_code === key || String(x.order_delivery_id) === key)
        if (o) {
          return {
            order_delivery_id: o.order_delivery_id, order_code: o.order_code, status: o.status, status_text: o.status_text,
            cancelled: o.status === '-1', source: o.source,
            set_menu: { id: 0, name: o.set_menu_name, meals_quota: o.meals, meals_remaining: 0, rounds: 1, round_no: 1 },
            delivery: { date: o.delivery_date, time: o.delivery_time, carrier: o.carrier, lala_no: null, completed_at: null, remark: null },
            meals: o.meals,
            lines: [
              { menu_id: 101, name: 'P1.กะเพราไก่สับ', qty: Math.ceil(o.meals / 2), type: 'sideDish', line_amount: 0, over_qty: 0 },
              { menu_id: 105, name: 'P2.ไก่ย่างโคชูจัง', qty: Math.floor(o.meals / 2), type: 'sideDish', line_amount: 0, over_qty: 0 },
            ],
            totals: { subtotal: o.total - 80, overset: 0, discount: 0, coupon_code: null, shipping_fee: 80, cod_fee: 0, total: o.total },
            payment: { method: 'BankTransfer', paid_at: `${o.delivery_date} 09:12`, amount: o.total },
            created_by: 'พลอย',
            bill_url: `https://cleanfit.example/order/${o.order_code}`,
          }
        }
      }
      throw new PosApiError(404, 'ไม่พบข้อมูล', 'notFound')
    })
  }

  /** Same arithmetic for quote and order. */
  price(body: PosCartBody): PosQuote {
    const cus = this.customers.get(String(body.customer_id))
    if (!cus) throw new PosApiError(422, 'ไม่พบลูกค้าใน POS', 'customerNotFound')
    let pack = 0
    let quota = 0
    let setName = ''
    let setId = 0
    let cont: PosQuote['continue'] = null
    if (body.continue_header_id) {
      const op = Object.values(this.history).flatMap((h) => h.open_packs).find((p) => p.order_header_id === body.continue_header_id)
      if (!op) throw new PosApiError(422, 'สั่งต่อไม่ได้: ไม่พบแพ็ก/ไม่เหลือสิทธิ์/ไม่ใช่ของลูกค้านี้', 'continueInvalid')
      quota = op.meals_remaining
      setName = op.set_menu_name
      cont = { order_header_id: op.order_header_id, meals_remaining_before: op.meals_remaining, meals_quota: op.meals_quota }
    } else {
      const s = MOCK_CATALOG.set_menus.find((x) => x.id === body.set_menu_id)
      if (!s) throw new PosApiError(422, 'แพ็กไม่ถูกต้องหรือปิดขาย', 'setMenuInvalid')
      pack = s.price
      quota = s.meals
      setName = s.name
      setId = s.id
    }
    if (!body.items.length) throw new PosApiError(422, 'รายการเมนูไม่ถูกต้อง', 'itemsInvalid')
    let plus = 0
    let over = 0
    let meals = 0
    let snacks = 0
    let snackAmt = 0
    const lines = body.items.map((it) => {
      const m = MOCK_CATALOG.menus.find((x) => x.id === it.menu_id)
      if (!m) throw new PosApiError(422, 'มีเมนูที่ปิดขายหรือไม่มีอยู่', 'menuClosed')
      if (m.type === 'snacks') {
        snacks += it.qty
        snackAmt += m.price * it.qty
        return { menu_id: m.id, name: m.name, type: m.type, qty: it.qty, price: m.price, plus: 0, line_amount: m.price * it.qty, over_qty: 0, over_amount: 0 }
      }
      const inside = Math.max(0, Math.min(it.qty, quota - meals))
      const overQty = it.qty - inside
      meals += it.qty
      plus += m.plus * it.qty
      over += overQty * m.price
      return { menu_id: m.id, name: m.name, type: m.type, qty: it.qty, price: m.price, plus: m.plus, line_amount: m.plus * it.qty, over_qty: overQty, over_amount: overQty * m.price }
    })
    let discount = 0
    if (body.coupon?.token) {
      const [, tokSet, disc] = body.coupon.token.split('.')
      if (tokSet !== String(body.set_menu_id ?? '')) throw new PosApiError(422, 'เปลี่ยนแพ็กหลังกรอกโค้ด ต้องกรอกโค้ดใหม่', 'couponStale')
      discount = Number(disc)
    }
    const feeSystem = body.shipping === 'dhl' ? 100 : cus.has_pin && !cus.outOfZone ? 65 : null
    const fee = body.shipping_fee ?? feeSystem ?? 0
    const cod = body.payment?.method === 'CashOnDelivery' ? MOCK_CATALOG.cod_fee : 0
    const d = this.cat.delivery as PosDelivery
    const warnings: string[] = []
    if (isLocked(d, body.delivery_date, body.shipping)) warnings.push(`วันส่ง ${body.delivery_date} ทีมยิงงาน Lalamove ไปแล้ว บันทึกไม่ได้`)
    const dow = new Date(Date.parse(`${body.delivery_date}T12:00:00+07:00`)).getUTCDay()
    if (body.shipping === 'lalamove' && dow === 6) warnings.push('วันเสาร์เว็บปกติไม่ส่ง Lalamove (หน้าขายไม่บังคับ) เช็คกับครัวก่อน')
    if (body.delivery_date === dayKey(this.now + DAY) && bangkokTime(this.now) >= '22:00') {
      warnings.push('เลยเวลาปิดรับออเดอร์ผ่านแชท (22:00 ของวันก่อนส่ง) แล้ว — เช็คกับครัว/ทีมยิงงานว่ายังทันรอบ')
    }
    const subtotal = pack + plus + over + snackAmt
    const total = fee + subtotal - discount + cod
    return {
      customer: { id: Number(cus.id), name: cus.name, mobile_masked: cus.mobile_masked, address: cus.address },
      staff: { id: 49, name: 'claude dev' },
      channel: { id: 5, type: 'line', name: 'Cleanfit' },
      set_menu: { id: setId, name: setName, price: pack, meals_quota: quota, freedom: false },
      continue: cont,
      lines,
      meals,
      snacks,
      shipping: {
        carrier: body.shipping, fee_system: feeSystem, fee, zone: body.shipping === 'dhl' ? null : 2,
        km: body.shipping === 'dhl' ? null : cus.has_pin ? 8.4 : null,
        reason: body.shipping === 'dhl' ? 'DHL โซนต่างจังหวัด' : !cus.has_pin ? 'ลูกค้ายังไม่มีหมุด'
          : cus.outOfZone ? 'หมุดอยู่นอก 4 จังหวัดโซน Lalamove' : 'หมุดห่างร้าน 8.4 กม.',
        location_id: 0,
      },
      totals: {
        pack, plus, snacks: snackAmt, overset: over, subtotal, discount, coupon_code: body.coupon?.code ?? '',
        shipping_fee: fee, cod_fee: cod, total, paid: body.payment ? total : null,
      },
      overrides: body.shipping_fee != null && body.shipping_fee !== feeSystem
        ? [{ field: 'shipping_fee', system: feeSystem, final: body.shipping_fee, reason: body.override_reason ?? '' }]
        : [],
      warnings,
      delivery: { date: body.delivery_date, time: body.delivery_time ?? d.default_time },
    }
  }

  quote(body: PosCartBody): Promise<PosQuote> {
    return this.tick('quote', body, () => this.price(body))
  }

  checkCoupon(body: PosCouponCheckBody): Promise<PosCouponCheck> {
    return this.tick('checkCoupon', body, () => {
      if (body.continue_header_id) {
        throw new PosApiError(422, 'การสั่งซื้ออาหารที่เหลือ ไม่สามารถใช้คูปองส่วนลดได้', 'couponNotAllowed')
      }
      const code = body.coupon_code.trim().toUpperCase()
      const c = COUPONS[code]
      if (!c) throw new PosApiError(422, 'โค้ดส่วนลดใช้ไม่ได้: ไม่พบข้อมูล', 'couponInvalid')
      const s = MOCK_CATALOG.set_menus.find((x) => x.id === body.set_menu_id)
      let base = s?.price ?? 0
      for (const it of body.items) base += (MOCK_CATALOG.menus.find((m) => m.id === it.menu_id)?.plus ?? 0) * it.qty
      const discount = c.unit === 'percen' ? Math.ceil((base * c.benefits) / 100) : c.benefits
      return {
        code, unit: c.unit, benefits: c.benefits, name: c.name, base_subtotal: base, discount, free_items: 0,
        token: `mock.${body.set_menu_id ?? ''}.${discount}`, expires_at: '2026-09-27T10:00:00+07:00',
        set_menu_id: body.set_menu_id ?? null,
      }
    })
  }

  createOrder(body: PosOrderBody): Promise<PosOrderResult> {
    return this.tick('createOrder', body, () => {
      const prev = this.orders.get(body.client_id)
      if (prev) return { ...prev, replayed: true }
      if (body.shipping_fee != null && (body.override_reason ?? '').trim().length < 3) {
        throw new PosApiError(422, 'แก้ค่าส่งต้องใส่เหตุผล (อย่างน้อย 3 ตัวอักษร)', 'overrideReasonRequired')
      }
      if (body.payment.method === 'BankTransfer' && !body.payment.paid_at) {
        throw new PosApiError(422, 'ใส่วันและเวลาที่ลูกค้าโอน', 'paymentInvalid')
      }
      if (body.payment.method === 'PendingTransfer' && body.continue_header_id) {
        throw new PosApiError(422, 'ข้อมูลการชำระเงินไม่ถูกต้อง: สั่งต่อแพ็กเดิมพักบิลรอโอนไม่ได้', 'paymentInvalid')
      }
      if (body.payment.method === 'CardLink' && body.continue_header_id) {
        throw new PosApiError(422, 'ข้อมูลการชำระเงินไม่ถูกต้อง: สั่งต่อแพ็กเดิมสร้างลิงก์ชำระบัตรไม่ได้', 'paymentInvalid')
      }
      if (body.payment.method === 'CardLink' && this.now >= cardExpiry(body.delivery_date)) {
        throw new PosApiError(422, 'ลิงก์บัตรของวันส่งนี้หมดเวลาแล้ว (ชำระได้ถึง 21:30 ของวันก่อนส่ง) — เลือกวันส่งใหม่ หรือใช้วิธีชำระอื่น', 'cardLinkTooLate')
      }
      // one open payment request per chat (api/app/pos_api.pay_conflict)
      const open = (this.chatOrders.get(body.conversation_id) ?? []).filter((o) => o.status === 'held')
      if (body.payment.method === 'CardLink' && open.length) {
        throw new PosApiError(409, open[0]!.card
          ? 'แชทนี้มีลิงก์ชำระบัตรเปิดอยู่แล้ว ลูกค้าขอเปลี่ยนรายการ กด "แก้ไขออเดอร์" ที่การ์ดลิงก์ ถ้าเป็นออเดอร์ใหม่อีกใบ ยกเลิกลิงก์เดิมก่อน (กันลูกค้าจ่ายซ้ำ)'
          : 'แชทนี้มีพักบิลรอโอนเปิดอยู่ เปลี่ยนเป็นจ่ายบัตร กด "ยกเลิกพักบิล" ที่การ์ดก่อน แล้วค่อยสร้างลิงก์ (กันลูกค้าจ่ายซ้ำ)', 'payRequestOpen')
      }
      if (body.payment.method === 'PendingTransfer' && open.some((o) => !!o.card)) {
        throw new PosApiError(409, 'แชทนี้มีลิงก์ชำระบัตรเปิดอยู่ เปลี่ยนเป็นโอน กด "ยกเลิกลิงก์" ที่การ์ดก่อน แล้วค่อยพักบิลรอโอน (กันลูกค้าจ่ายซ้ำ)', 'payRequestOpen')
      }
      if (isLocked(this.cat.delivery as PosDelivery, body.delivery_date, body.shipping)) {
        throw new PosApiError(409, 'วันส่งนี้ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว) เลือกวันอื่น', 'dayLocked')
      }
      if (body.shipping === 'lalamove' && this.customers.get(String(body.customer_id))?.has_pin === false) {
        throw new PosApiError(422, 'ลูกค้ายังไม่มีหมุด ส่ง Lalamove ต้องมีหมุด ใส่หมุดของลูกค้าก่อนบันทึก', 'pinRequired')
      }
      const q = this.price(body)
      if (q.shipping.fee_system === null && body.shipping_fee == null) {
        throw new PosApiError(422, 'ระบบคิดค่าส่งไม่ได้ (นอกโซน) ต้องใส่ค่าส่งเองพร้อมเหตุผล', 'shipFeeUnknown')
      }
      this.seq += 1
      if (body.payment.method === 'PendingTransfer') return this.hold(body, q)
      if (body.payment.method === 'CardLink') return this.card(body, q)
      const code = `20260926-${String(this.seq).padStart(2, '0')}`
      const bill = `https://cleanfit.cloud/beta/buy/order/mock${this.seq}`
      const ref: ChatOrderRef = {
        id: String(this.seq), client_id: body.client_id, status: 'done', order_code: code,
        order_delivery_id: String(280430 + this.seq), bill_url: bill, total: q.totals.total,
        customer_id: String(body.customer_id), staff_name: 'พลอย', error_message: null, created_at: new Date().toISOString(),
      }
      const res: PosOrderResult = {
        chat_order: ref,
        order: { replayed: false, order_delivery_id: 280430 + this.seq, order_code: code, bill_url: bill, order: q, warnings: [] },
        replayed: false,
      }
      this.orders.set(body.client_id, res)
      const list = this.chatOrders.get(body.conversation_id) ?? []
      list.unshift(ref)
      this.chatOrders.set(body.conversation_id, list)
      const conv = this.conv(body.conversation_id)
      if (!conv.customer) conv.customer = String(body.customer_id)
      return res
    })
  }

  /** The held order's cart as the edit form gets it (the server reads it back from the stored POS body). */
  private holdCart(body: PosCartBody, q: PosQuote): PosHoldCart {
    return {
      set_menu_id: body.set_menu_id ?? null,
      items: body.items.map((i) => ({ menu_id: i.menu_id, qty: i.qty })),
      shipping: body.shipping,
      location_id: body.location_id ?? null,
      delivery_date: body.delivery_date,
      delivery_time: body.delivery_time ?? null,
      remark: body.remark ?? '',
      spoon: !!body.spoon,
      coupon: body.coupon?.code
        ? { code: body.coupon.code, token: body.coupon.token ?? null, discount: q.totals.discount, free_items: q.totals.free_items ?? 0 }
        : null,
      shipping_fee: body.shipping_fee ?? null,
      override_reason: body.shipping_fee != null ? (body.override_reason ?? null) : null,
    }
  }

  private dueOf(date: string): { pay_due_at: string; late: boolean } {
    const due = Date.parse(`${date}T22:00:00+07:00`) - 86400000   // chat_v1: ปิดรับผ่านแชท 22:00 ของวันก่อนส่ง
    return { pay_due_at: new Date(due).toISOString(), late: this.now >= due }
  }

  /** PendingTransfer: a held bill (no order code / bill yet) — "ยืนยันชำระ" makes the order. */
  private hold(body: PosOrderBody, q: PosQuote): PosOrderResult {
    const ref: ChatOrderRef = {
      id: String(this.seq), client_id: body.client_id, status: 'held', order_code: null, order_delivery_id: null,
      bill_url: null, total: q.totals.total, customer_id: String(body.customer_id), staff_name: 'พลอย', error_message: null,
      created_at: new Date().toISOString(),
      hold: {
        id: String(36 + this.seq), code: `AB${36 + this.seq}021530`, ...this.dueOf(body.delivery_date),
        delivery_date: body.delivery_date, delivery_time: body.delivery_time ?? null,
        shipping: body.shipping, qr_media_id: null, edit_count: 0, edited_at: null, edited_by_name: null, qr_resend: false,
        cart: this.holdCart(body, q),
      },
      paid: null,
    }
    const res: PosOrderResult = { chat_order: ref, order: { replayed: false, order: { ...q, totals: { ...q.totals, paid: null } }, warnings: [] }, replayed: false }
    this.orders.set(body.client_id, res)
    const list = this.chatOrders.get(body.conversation_id) ?? []
    list.unshift(ref)
    this.chatOrders.set(body.conversation_id, list)
    this.chatStates.set(ref.id, { ...res, open_order: null })
    const conv = this.conv(body.conversation_id)
    if (!conv.customer) conv.customer = String(body.customer_id)
    return res
  }

  /** A card link of the mock POS (chat_v1 credits.php): expires 21:30 the day before delivery. */
  private cardOf(body: PosCartBody, q: PosQuote, prev?: PosCard | null): PosCard {
    this.cardSeq += 1
    const id = 28 + this.cardSeq
    const code = `CARD${id}1530`
    const exp = cardExpiry(body.delivery_date)
    return {
      id: String(id), code, link: `https://cleanfit.cloud/beta/checkout?code=${code}`, state: 'open',
      expire_at: new Date(exp).toISOString(), expired: this.now >= exp, close_time: '21:30',
      paying_since: null, paying_until: null, paid_at: null,
      pay_safe: this.cardPaySafe, test_note: this.cardPaySafe ? null : 'ลิงก์ beta ยังต่อ Beam จริง ห้ามจ่ายจนตั้งโหมดทดสอบ',
      delivery_date: body.delivery_date, delivery_time: body.delivery_time ?? null, shipping: body.shipping,
      edit_count: prev ? prev.edit_count + 1 : 0, edited_at: prev ? new Date().toISOString() : null,
      edited_by_name: prev ? 'พลอย' : null, link_resend: prev ? prev.link_resend || this.linkSent : false,
      renewed_at: null, renewed_by_name: null, cancelled_at: null, cancelled_by_name: null,
      checked_at: new Date().toISOString(), cart: this.holdCart(body, q),
    }
  }

  /** CardLink: a card payment link (no order code / bill until the customer pays it). */
  private card(body: PosOrderBody, q: PosQuote): PosOrderResult {
    const ref: ChatOrderRef = {
      id: String(this.seq), client_id: body.client_id, status: 'held', order_code: null, order_delivery_id: null,
      bill_url: null, total: q.totals.total, customer_id: String(body.customer_id), staff_name: 'พลอย', error_message: null,
      created_at: new Date().toISOString(), hold: null, card: this.cardOf(body, q), paid: null,
    }
    const res: PosOrderResult = { chat_order: ref, order: { replayed: false, order: { ...q, totals: { ...q.totals, paid: null } }, warnings: [] }, replayed: false }
    this.orders.set(body.client_id, res)
    const list = this.chatOrders.get(body.conversation_id) ?? []
    list.unshift(ref)
    this.chatOrders.set(body.conversation_id, list)
    this.chatStates.set(ref.id, { ...res, open_order: null })
    const conv = this.conv(body.conversation_id)
    if (!conv.customer) conv.customer = String(body.customer_id)
    return res
  }

  /** Test hook: the customer paid the link on the checkout page (the POS payhub made the order). */
  payCard(id: string): ChatOrderRef {
    const { ref } = this.heldRef(id)
    if (!ref.card) throw new Error('not a card link')
    const code = `20260926-${String(this.seq).padStart(2, '0')}`
    Object.assign(ref, {
      status: 'done', order_code: code, order_delivery_id: String(280430 + this.seq),
      bill_url: `https://cleanfit.cloud/beta/buy/order/mock${this.seq}`,
      paid: { via: 'card', at: new Date().toISOString(), amount: ref.total, by_name: null, slip_message_id: null, confirmed_at: null },
    })
    ref.card = { ...ref.card, state: 'paid', expired: false, paid_at: new Date().toISOString(), cart: null }
    const prev = this.chatStates.get(id)
    this.chatStates.set(id, { chat_order: ref, order: { ...(prev?.order ?? {}), order_code: code, bill_url: ref.bill_url ?? undefined }, replayed: false, open_order: null })
    return ref
  }

  cancelChatOrder(id: string): Promise<PosChatOrderState> {
    return this.tick('cancelChatOrder', id, () => {
      const { ref } = this.heldRef(id)
      if (ref.status === 'done') throw new PosApiError(409, `ออเดอร์นี้ยืนยันชำระแล้ว (ใบ ${ref.order_code})`, 'orderPaid')
      if (ref.status !== 'held') throw new PosApiError(409, ref.error_message ?? 'ออเดอร์นี้ไม่ได้รอชำระ', 'holdRemoved')
      if (ref.card?.paying_until && Date.parse(ref.card.paying_until) > Date.now()) {
        throw new PosApiError(409, `ลูกค้าเริ่มชำระผ่านลิงก์นี้ (บัตรหรือ QR ที่ออกไปแล้วยังจ่ายได้) ยกเลิกหรือแก้ไขได้หลัง ${bangkokTime(Date.parse(ref.card.paying_until))} น. ถ้าลูกค้าจ่ายสำเร็จ ระบบออกใบให้เอง`, 'cardPaying')
      }
      const at = bangkokTime(Date.now())
      Object.assign(ref, {
        status: 'void',
        error_message: `${ref.card ? 'ยกเลิกลิงก์ชำระบัตรแล้ว' : 'ยกเลิกพักบิลแล้ว'} โดย พลอย เมื่อ ${at} น. ไม่มีออเดอร์เกิดขึ้น ลงออเดอร์ใหม่ได้`,
      })
      if (ref.card) ref.card = { ...ref.card, state: 'cancelled', cancelled_at: new Date().toISOString(), cancelled_by_name: 'พลอย', cart: null }
      const st: PosChatOrderState = { chat_order: ref, order: this.chatStates.get(id)?.order ?? {}, replayed: false, open_order: null }
      this.chatStates.set(id, st)
      return st
    })
  }

  renewChatOrder(id: string, body: PosRenewBody): Promise<PosChatOrderState> {
    return this.tick('renewChatOrder', { id, ...body }, () => {
      const { ref } = this.heldRef(id)
      if (ref.status !== 'held' || !ref.card) throw new PosApiError(409, 'ออเดอร์นี้ไม่ได้รอชำระด้วยลิงก์บัตร', 'notCard')
      if (this.now >= cardExpiry(body.delivery_date)) {
        throw new PosApiError(422, 'ลิงก์บัตรของวันส่งนี้หมดเวลาแล้ว (ชำระได้ถึง 21:30 ของวันก่อนส่ง) — เลือกวันส่งใหม่ หรือใช้วิธีชำระอื่น', 'cardLinkTooLate')
      }
      if (isLocked(this.cat.delivery as PosDelivery, body.delivery_date, ref.card.shipping ?? 'lalamove')) {
        throw new PosApiError(409, 'วันส่งนี้ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว) เลือกวันอื่น', 'dayLocked')
      }
      const exp = cardExpiry(body.delivery_date)
      const time = ref.card.shipping === 'dhl' ? ref.card.delivery_time : (body.delivery_time ?? ref.card.delivery_time)
      ref.card = {
        ...ref.card, state: 'open', expired: false, expire_at: new Date(exp).toISOString(), delivery_date: body.delivery_date,
        delivery_time: time, renewed_at: new Date().toISOString(), renewed_by_name: 'พลอย',
        cart: ref.card.cart ? { ...ref.card.cart, delivery_date: body.delivery_date, delivery_time: time } : null,
      }
      const st: PosChatOrderState = { chat_order: ref, order: this.chatStates.get(id)?.order ?? {}, replayed: false, open_order: null }
      this.chatStates.set(id, st)
      return st
    })
  }

  refreshChatOrder(id: string): Promise<PosChatOrderState> {
    return this.tick('refreshChatOrder', id, () => {
      const { ref } = this.heldRef(id)
      return { chat_order: ref, order: this.chatStates.get(id)?.order ?? {}, replayed: false, open_order: null }
    })
  }

  private heldRef(id: string): { ref: ChatOrderRef; conv: string } {
    for (const [conv, list] of this.chatOrders) {
      const ref = list.find((o) => o.id === id)
      if (ref) return { ref, conv }
    }
    throw new PosApiError(404, 'ไม่พบออเดอร์นี้', 'notFound')
  }

  promptpayQr(id: string): Promise<PosQr> {
    return this.tick('promptpayQr', id, () => {
      const { ref } = this.heldRef(id)
      if (ref.status !== 'held' || !ref.hold) throw new PosApiError(409, 'ออเดอร์นี้ไม่ได้รอชำระ', 'notHeld')
      const amount = ref.total ?? 0
      if (!ref.hold.qr_media_id) {
        // a new QR (after an edit: a new picture for the new amount) — the "send the new QR" note goes
        this.qrSeq += 1
        ref.hold.qr_media_id = `qr${id}-${this.qrSeq}`
        ref.hold.qr_resend = false
      }
      return {
        asset: {
          id: ref.hold.qr_media_id, kind: 'image', title: `QR พร้อมเพย์ ยอด ${amount.toLocaleString('en-US')} บาท`, folder: '',
          url: `https://media.example/m/${ref.hold.qr_media_id}`, preview_url: null, mime: 'image/png', bytes: 2400, width: 600, height: 600,
          duration_ms: null, created_at: new Date().toISOString(), created_by: 'mock', created_by_name: null, used_in: [],
          send_count: 0, last_sent_at: null, limits: [], deleted: false,
        },
        amount,
        chat_order: { chat_order: ref, order: {}, replayed: false, open_order: null },
        payment_text: '',
      } as PosQr
    })
  }

  payChatOrder(id: string, body: PosPayBody): Promise<PosChatOrderState & { replayed?: boolean }> {
    return this.tick('payChatOrder', { id, ...body }, () => {
      const { ref } = this.heldRef(id)
      if (ref.status === 'done') throw new PosApiError(409, `ออเดอร์นี้ยืนยันชำระแล้ว (ใบ ${ref.order_code})`, 'orderPaid')
      if (ref.status !== 'held') throw new PosApiError(409, 'ออเดอร์นี้ไม่ได้รอชำระ', 'notHeld')
      const total = ref.total ?? 0
      const diff = Math.round((body.amount - total) * 100) / 100
      if (Math.abs(diff) > 0.001 && !body.accept_diff) {
        throw new PosApiError(409, `ยอดโอน${diff > 0 ? 'เกิน' : 'ขาด'}ยอดที่ต้องชำระ ${Math.abs(diff)} บาท`, 'amountMismatch',
          { total, amount: body.amount, diff })
      }
      const code = `20260926-${String(this.seq).padStart(2, '0')}`
      Object.assign(ref, {
        status: 'done', order_code: code, order_delivery_id: String(280430 + this.seq),
        bill_url: `https://cleanfit.cloud/beta/buy/order/mock${this.seq}`,
        paid: { via: 'chat', at: `${body.paid_at.replace(' ', 'T')}:00+07:00`, amount: body.amount, by_name: 'พลอย',
          slip_message_id: body.slip_message_id ?? null, confirmed_at: new Date().toISOString() },
      })
      const prev = this.chatStates.get(id)
      const st: PosChatOrderState = { chat_order: ref, order: { ...(prev?.order ?? {}), order_code: code, bill_url: ref.bill_url ?? undefined }, replayed: false, open_order: null }
      this.chatStates.set(id, st)
      return st
    })
  }

  /**
   * "แก้ไขออเดอร์": the same hold (id, code) with the new cart and total, the QR to be made again. The mock cannot see
   * sends: a QR made counts as sent (`qrSent` = false in a test says it never left).
   */
  editChatOrder(id: string, body: PosHoldEditBody): Promise<PosChatOrderState & { replayed?: boolean }> {
    return this.tick('editChatOrder', { id, ...body }, () => {
      const prev = this.edits.get(`${id}|${body.client_id}`)
      if (prev) return { ...prev, replayed: true }
      const { ref } = this.heldRef(id)
      if (ref.status === 'done') throw new PosApiError(409, `ออเดอร์นี้ยืนยันชำระแล้ว (ใบ ${ref.order_code})`, 'orderPaid')
      if (ref.status === 'held' && ref.card) return this.editCard(id, ref, body)
      if (ref.status !== 'held' || !ref.hold) throw new PosApiError(409, 'ออเดอร์นี้ไม่ได้รอชำระ', 'notHeld')
      if (body.payment.method !== 'PendingTransfer') {
        throw new PosApiError(422, 'แก้ไขพักบิลได้เฉพาะแบบยังไม่โอน (ลูกค้าโอนแล้วให้กดยืนยันชำระ)', 'paymentInvalid')
      }
      if (String(body.customer_id) !== ref.customer_id) {
        throw new PosApiError(422, 'แก้ไขพักบิลเปลี่ยนลูกค้าไม่ได้ ถ้าเป็นลูกค้าคนอื่น ให้ลงออเดอร์ใหม่', 'customerChanged')
      }
      if (body.edit_count != null && body.edit_count !== (ref.hold.edit_count ?? 0)) {
        throw new PosApiError(409, `พักบิลนี้ถูกแก้ไปแล้วโดย ${ref.hold.edited_by_name ?? 'ทีม'} กดยกเลิกแล้วเปิดแก้ไขใหม่ เพื่อแก้จากข้อมูลล่าสุด`, 'holdEdited')
      }
      if (body.continue_header_id) throw new PosApiError(422, 'ข้อมูลการชำระเงินไม่ถูกต้อง: สั่งต่อแพ็กเดิมพักบิลรอโอนไม่ได้', 'paymentInvalid')
      if (isLocked(this.cat.delivery as PosDelivery, body.delivery_date, body.shipping)) {
        throw new PosApiError(409, 'วันส่งนี้ปิดรับแล้ว (ทีมยิงงาน Lalamove ของวันนั้นไปแล้ว) เลือกวันอื่น', 'dayLocked')
      }
      if (body.shipping === 'lalamove' && this.customers.get(String(body.customer_id))?.has_pin === false) {
        throw new PosApiError(422, 'ลูกค้ายังไม่มีหมุด ส่ง Lalamove ต้องมีหมุด ใส่หมุดของลูกค้าก่อนบันทึก', 'pinRequired')
      }
      const q = this.price(body)
      const sent = !!ref.hold.qr_media_id && this.qrSent
      ref.total = q.totals.total
      ref.hold = {
        ...ref.hold, ...this.dueOf(body.delivery_date), delivery_date: body.delivery_date,
        delivery_time: body.delivery_time ?? null, shipping: body.shipping, qr_media_id: null,
        qr_resend: !!ref.hold.qr_resend || sent, edit_count: (ref.hold.edit_count ?? 0) + 1,
        edited_at: new Date().toISOString(), edited_by_name: 'พลอย', cart: this.holdCart(body, q),
      }
      const st: PosChatOrderState = {
        chat_order: ref, order: { replayed: false, order: { ...q, totals: { ...q.totals, paid: null } }, warnings: [] },
        replayed: false, open_order: null,
      }
      this.chatStates.set(id, st)
      this.edits.set(`${id}|${body.client_id}`, clone(st))
      return st
    })
  }

  /** "แก้ไขออเดอร์" of a card link: the old link cancelled, a new one (new id/code) on the same chat order. */
  private editCard(id: string, ref: ChatOrderRef, body: PosHoldEditBody): PosChatOrderState {
    const card = ref.card as PosCard
    if (body.payment.method !== 'CardLink') {
      throw new PosApiError(422, 'แก้ไขลิงก์ชำระบัตรได้เฉพาะแบบบัตร (เปลี่ยนวิธีชำระ ให้ยกเลิกลิงก์ก่อนแล้วลงออเดอร์ใหม่)', 'paymentInvalid')
    }
    if (String(body.customer_id) !== ref.customer_id) {
      throw new PosApiError(422, 'แก้ไขลิงก์เปลี่ยนลูกค้าไม่ได้ ถ้าเป็นลูกค้าคนอื่น ให้ยกเลิกลิงก์แล้วลงออเดอร์ใหม่', 'customerChanged')
    }
    if (body.edit_count != null && body.edit_count !== card.edit_count) {
      throw new PosApiError(409, `ลิงก์นี้ถูกแก้ไปแล้วโดย ${card.edited_by_name ?? 'ทีม'} กดยกเลิกแล้วเปิดแก้ไขใหม่ เพื่อแก้จากข้อมูลล่าสุด`, 'holdEdited')
    }
    if (card.paying_until && Date.parse(card.paying_until) > Date.now()) {
      throw new PosApiError(409, `ลูกค้าเริ่มชำระผ่านลิงก์นี้ ยกเลิกหรือแก้ไขได้หลัง ${bangkokTime(Date.parse(card.paying_until))} น.`, 'cardPaying')
    }
    if (this.now >= cardExpiry(body.delivery_date)) {
      throw new PosApiError(422, 'ลิงก์บัตรของวันส่งนี้หมดเวลาแล้ว (ชำระได้ถึง 21:30 ของวันก่อนส่ง) — เลือกวันส่งใหม่ หรือใช้วิธีชำระอื่น', 'cardLinkTooLate')
    }
    const q = this.price(body)
    ref.total = q.totals.total
    ref.card = this.cardOf(body, q, card)
    const st: PosChatOrderState = {
      chat_order: ref, order: { replayed: false, order: { ...q, totals: { ...q.totals, paid: null } }, warnings: [] },
      replayed: false, open_order: null,
    }
    this.chatStates.set(id, st)
    this.edits.set(`${id}|${body.client_id}`, clone(st))
    return st
  }

  chatOrder(id: string): Promise<PosChatOrderState> {
    return this.tick('chatOrder', id, () => {
      const st = this.chatStates.get(id)
      if (!st) throw new PosApiError(404, 'ไม่พบออเดอร์นี้', 'notFound')
      return st
    })
  }

  checkChatOrder(id: string): Promise<PosChatOrderState> {
    return this.tick('checkChatOrder', id, () => {
      const st = this.chatStates.get(id)
      if (!st) throw new PosApiError(404, 'ไม่พบออเดอร์นี้', 'notFound')
      return st
    })
  }

  abandonChatOrder(id: string): Promise<PosChatOrderState> {
    return this.tick('abandonChatOrder', id, () => {
      for (const [conv, o] of this.openOrders) {
        if (o.id !== id) continue
        this.openOrders.delete(conv)
        const st: PosChatOrderState = {
          chat_order: { ...o, status: 'abandoned', error_message: 'ยกเลิกคำขอโดย ทดสอบ หลังตรวจใน POS แล้วว่าไม่มีออเดอร์นี้' },
          order: {},
          replayed: false,
          followed_up: true,
          open_order: null,
        }
        this.chatStates.set(id, st)
        return st
      }
      throw new PosApiError(409, 'คำขอนี้รู้ผลแล้ว ไม่ต้องยกเลิก', 'orderSettled')
    })
  }
}
